import React, { useContext, useEffect, useState } from 'react'
import { observer } from 'mobx-react'
import { useLocation, useNavigate, useParams } from 'react-router'
import { StoresContext } from '@App/contexts'
import { PARAM } from '@App/navigation/param'
import { SongwriterSubmissionSuggestion } from '@Entities/rightsholder/songwriter-submission-suggestion'
import {
    getSongwriterSubmissionSuggestions,
    updateSongwriterSubmissionSuggestionStatus,
} from '@App/api/routes/rightsholder/change-requests'
import { FetchType } from '@Components/WorkItemV2/fetch-type-enum'
import PointPreloader from '@Components/Preloaders/PointPreloader'
import ViewWork from '@Components/ViewWork'
import Navigation from '@App/navigation'
import './index.scss'
import ChangeRequestViewHeader from '@Scenes/RightsholderScene/ChangeRequests/View/ChangeRequestViewHeader/index'

interface LocationState {
    suggestion?: SongwriterSubmissionSuggestion
}

const ChangeRequestView = () => {
    const { rightsholderStore, popupStore, workStore } = useContext(StoresContext)
    const params = useParams<typeof PARAM>()
    const location = useLocation()
    const navigate = useNavigate()
    const [suggestion, setSuggestion] = useState<SongwriterSubmissionSuggestion>()
    const [loading, setLoading] = useState(true)

    const suggestionFromState = (location.state as LocationState | null)?.suggestion
    const rightsholderId = rightsholderStore.currentRightsholderId
    const suggestionId = Number(params.suggestionId)
    const work = workStore.workById

    const load = async () => {
        setLoading(true)

        try {
            const selectedSuggestion =
                suggestionFromState ??
                (
                    await getSongwriterSubmissionSuggestions(rightsholderId, ['SUBMITTED', 'APPROVED', 'DISMISSED'], {
                        page: 1,
                        limit: 100,
                    })
                ).data.content.find((item) => item.id === suggestionId)

            if (!selectedSuggestion) {
                return
            }

            await workStore.getWorkById(Number(selectedSuggestion.submissionId), FetchType.MLC)
            setSuggestion(selectedSuggestion)
        } catch (e) {
            popupStore.setAlertFromError(e)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        void load()
    }, [rightsholderId, suggestionId])

    const updateStatus = async (status: 'APPROVED' | 'DISMISSED') => {
        try {
            await updateSongwriterSubmissionSuggestionStatus(rightsholderId, suggestionId, status)
            navigate(Navigation.rightholder.catalog.changeRequests.root(rightsholderId))
        } catch (e) {
            popupStore.setAlertFromError(e)
        }
    }

    if (!suggestion || !work) {
        return null
    }

    return loading ? (
        <PointPreloader />
    ) : (
        <div className="change-request-view-scene">
            <ChangeRequestViewHeader
                suggestion={suggestion}
                work={work}
                rightsholderId={rightsholderId}
                updateStatus={updateStatus}
            />
            <h2 className="work-title">{work.title}</h2>
            <ViewWork viewWork={work} workType="history" />
        </div>
    )
}

export default observer(ChangeRequestView)
